<template>
  <v-card dark flat class="subtool-surface">
    <div class="surface-header">
      <div class="title-group">
        <span class="title-icon"
          ><v-icon>{{ icon }}</v-icon></span
        >
        <div>
          <div class="surface-eyebrow">{{ eyebrow }}</div>
          <h2>{{ title }}</h2>
          <p>{{ description }}</p>
        </div>
      </div>
      <span class="result-pill">{{ selectedCount }} von {{ items.length }} aktiv</span>
    </div>

    <div v-if="items.length" class="selection-grid">
      <v-card
        v-for="item in items"
        :key="item._id"
        dark
        flat
        class="selection-card"
        :class="{ 'is-selected': item.isSelected }"
        role="switch"
        tabindex="0"
        :aria-checked="String(Boolean(item.isSelected))"
        @click="$emit('toggle', item)"
        @keydown.enter="$emit('toggle', item)"
        @keydown.space.prevent="$emit('toggle', item)">
        <span class="selection-icon"
          ><v-icon>{{ item.icon || fallbackIcon }}</v-icon></span
        >
        <span class="state-pill" :class="{ active: item.isSelected }"
          ><v-icon small>{{ item.isSelected ? "mdi-check" : "mdi-plus" }}</v-icon
          >{{ item.isSelected ? "Aktiv" : "Hinzufügen" }}</span
        >
        <h3>{{ item.title }}</h3>
        <p>{{ item.description }}</p>
      </v-card>
    </div>

    <div v-else class="surface-empty">
      <span
        ><v-icon>{{ icon }}</v-icon></span
      >
      <h3>{{ emptyTitle }}</h3>
      <p>{{ emptyText }}</p>
    </div>
  </v-card>
</template>

<script>
export default {
  name: "WebsiteSelectionGrid",
  props: {
    items: { type: Array, default: () => [] },
    eyebrow: { type: String, default: "Website-Angebot" },
    title: { type: String, required: true },
    description: { type: String, required: true },
    icon: { type: String, required: true },
    fallbackIcon: { type: String, required: true },
    emptyTitle: { type: String, required: true },
    emptyText: { type: String, required: true },
  },
  computed: {
    selectedCount() {
      return this.items.filter((item) => item.isSelected).length
    },
  },
}
</script>

<style scoped>
.subtool-surface {
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --muted: #a3a3a8;
  padding: 1.375rem;
  border: 1px solid var(--border);
  border-radius: 1.375rem !important;
  background: rgba(255, 255, 255, 0.03) !important;
  box-shadow: 0 1rem 2.75rem rgba(0, 0, 0, 0.12);
  font-size: 1rem;
}

.surface-header,
.title-group,
.state-pill {
  display: flex;
  align-items: center;
}

.surface-header {
  justify-content: space-between;
  gap: 1.125rem;
  margin-bottom: 1.125rem;
}

.title-group {
  min-width: 0;
  gap: 0.75rem;
}

.title-icon,
.selection-icon,
.surface-empty > span {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
}

.title-icon {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.8125rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.11);
}

.title-icon .v-icon,
.selection-icon .v-icon,
.surface-empty > span .v-icon {
  color: currentColor;
}

.surface-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.surface-header h2 {
  margin: 0.1875rem 0 0;
  color: var(--text);
  font-size: 1.25rem;
}

.surface-header p,
.selection-card p,
.surface-empty p {
  margin: 0.25rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.5;
}

.result-pill {
  min-height: 1.75rem;
  padding: 0.25rem 0.625rem;
  border: 1px solid var(--border);
  border-radius: 999px;
  color: #d4d4d8;
  background: rgba(255, 255, 255, 0.055);
  font-size: 0.75rem;
  font-weight: 750;
  white-space: nowrap;
}

.selection-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}

.selection-card {
  position: relative;
  min-height: 12.5rem;
  padding: 1rem;
  border: 1px solid var(--border);
  border-radius: 1.0625rem !important;
  outline: none;
  background: rgba(255, 255, 255, 0.035) !important;
  transition: transform 160ms ease, border-color 160ms ease, background-color 160ms ease;
}

.selection-card.is-selected {
  border-color: rgba(0, 150, 64, 0.58);
  background: linear-gradient(145deg, rgba(0, 150, 64, 0.12), rgba(255, 255, 255, 0.035)) !important;
}

.selection-card:focus-visible {
  border-color: rgba(0, 150, 64, 0.68);
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.13);
}

.selection-icon {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.8125rem;
  color: #c5c5c8;
  background: rgba(255, 255, 255, 0.065);
}

.is-selected .selection-icon {
  color: #58d388;
  background: rgba(0, 150, 64, 0.14);
}

.state-pill {
  position: absolute;
  top: 1rem;
  right: 1rem;
  min-height: 1.625rem;
  gap: 0.25rem;
  padding: 0.1875rem 0.5rem;
  border: 1px solid var(--border);
  border-radius: 999px;
  color: var(--muted);
  background: rgba(255, 255, 255, 0.04);
  font-size: 0.75rem;
  font-weight: 700;
}

.state-pill.active {
  border-color: rgba(0, 150, 64, 0.28);
  color: #58d388;
  background: rgba(0, 150, 64, 0.1);
}

.state-pill .v-icon {
  color: currentColor;
}

.selection-card h3,
.surface-empty h3 {
  margin: 1rem 0 0;
  color: var(--text);
  font-size: 1rem;
}

.surface-empty {
  display: flex;
  min-height: 16rem;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
}

.surface-empty > span {
  width: 3.5rem;
  height: 3.5rem;
  margin-bottom: 0.75rem;
  border-radius: 1rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.11);
}

@media (hover: hover) and (pointer: fine) {
  .selection-card:hover {
    transform: translateY(-2px);
    border-color: var(--border-strong);
  }
}

@media (max-width: 59.9375rem) {
  .selection-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 37.4375rem) {
  .subtool-surface {
    padding: 0.875rem;
    border-radius: 1.125rem !important;
  }

  .surface-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.625rem;
  }

  .selection-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
